import { c as _c } from "react/compiler-runtime";
import { ChevronsUpDown, CircleQuestionMarkIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { ViewMode } from '../_schemas/graph';
interface GraphLegendProps {
  dir: 'ltr' | 'rtl';
  viewMode: ViewMode;
}
type LegendId = 'articulationMuhaqqaq' | 'articulationMuqaddar' | 'traitLazimah' | 'traitAridhah';
export function GraphLegend(t0) {
  const $ = _c(80);
  const {
    dir,
    viewMode
  } = t0;
  const t = useTranslations("playground_page.legend");
  const [isOpen, setIsOpen] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedLegendId, setSelectedLegendId] = useState(null);
  let T0;
  let T1;
  let legendInfo;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== dir || $[1] !== isOpen || $[2] !== t || $[3] !== viewMode) {
    const legends = [...(["all", "articulation", "letter"].includes(viewMode) ? [{
      id: "articulationArea",
      label: t("articulation_area"),
      color: "from-node-articulation to-node-articulation-dark"
    }] : []), ...(["all", "articulation", "letter"].includes(viewMode) ? [{
      id: "articulationMuhaqqaq",
      label: t("articulation_muhaqqaq"),
      color: "border-2 border-node-articulation/50 bg-node-articulation/20",
      hasInfo: true
    }] : []), ...(["all", "articulation", "letter"].includes(viewMode) ? [{
      id: "articulationMuqaddar",
      label: t("articulation_muqaddar"),
      color: "border-2 border-dashed border-node-articulation/50 bg-node-articulation/20",
      hasInfo: true
    }] : []), ...(["all", "trait", "letter"].includes(viewMode) ? [{
      id: "traitGroup",
      label: t("trait_group"),
      color: "from-node-trait to-node-trait-dark"
    }] : []), ...(["all", "trait", "letter"].includes(viewMode) ? [{
      id: "traitLazimah",
      label: t("trait_lazimah"),
      color: "border-2 border-node-trait/50 bg-node-trait/20",
      hasInfo: true
    }] : []), ...(["all", "trait", "letter"].includes(viewMode) ? [{
      id: "traitAridhah",
      label: t("trait_aridhah"),
      color: "border-2 border-dashed border-node-trait/50 bg-node-trait/20",
      hasInfo: true
    }] : []), {
      id: "letter",
      label: t("letter"),
      color: "from-node-letter to-node-letter-dark"
    }, ...(["all", "articulation", "letter"].includes(viewMode) ? [{
      id: "letterMadd",
      label: t("letterMadd"),
      color: "from-node-letter-madd to-node-letter-madd-dark"
    }] : [])];
    let t10;
    if ($[16] !== t) {
      t10 = t("articulation_muhaqqaq");
      $[16] = t;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== t) {
      t11 = t("articulation_muhaqqaq_description");
      $[18] = t;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    let t12;
    if ($[20] !== t10 || $[21] !== t11) {
      t12 = {
        title: t10,
        description: t11
      };
      $[20] = t10;
      $[21] = t11;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    let t13;
    if ($[23] !== t) {
      t13 = t("articulation_muqaddar");
      $[23] = t;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    let t14;
    if ($[25] !== t) {
      t14 = t("articulation_muqaddar_description");
      $[25] = t;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    let t15;
    if ($[27] !== t13 || $[28] !== t14) {
      t15 = {
        title: t13,
        description: t14
      };
      $[27] = t13;
      $[28] = t14;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    let t16;
    if ($[30] !== t) {
      t16 = t("trait_lazimah");
      $[30] = t;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    let t17;
    if ($[32] !== t) {
      t17 = t("trait_lazimah_description");
      $[32] = t;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    let t18;
    if ($[34] !== t16 || $[35] !== t17) {
      t18 = {
        title: t16,
        description: t17
      };
      $[34] = t16;
      $[35] = t17;
      $[36] = t18;
    } else {
      t18 = $[36];
    }
    let t19;
    if ($[37] !== t) {
      t19 = t("trait_aridhah");
      $[37] = t;
      $[38] = t19;
    } else {
      t19 = $[38];
    }
    let t20;
    if ($[39] !== t) {
      t20 = t("trait_aridhah_description");
      $[39] = t;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    let t21;
    if ($[41] !== t19 || $[42] !== t20) {
      t21 = {
        title: t19,
        description: t20
      };
      $[41] = t19;
      $[42] = t20;
      $[43] = t21;
    } else {
      t21 = $[43];
    }
    let t22;
    if ($[44] !== t12 || $[45] !== t15 || $[46] !== t18 || $[47] !== t21) {
      t22 = {
        articulationMuhaqqaq: t12,
        articulationMuqaddar: t15,
        traitLazimah: t18,
        traitAridhah: t21
      };
      $[44] = t12;
      $[45] = t15;
      $[46] = t18;
      $[47] = t21;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    legendInfo = t22;
    T1 = Collapsible;
    t4 = "tour-graph-legend";
    t5 = dir;
    t6 = isOpen;
    t7 = setIsOpen;
    t8 = "flex w-48 flex-col gap-2 rounded-lg border bg-background";
    const t23 = isOpen ? "border-b" : "";
    let t24;
    if ($[49] !== t23) {
      t24 = cn("flex items-center justify-between gap-2 p-2 text-left font-semibold text-muted-foreground text-xs", t23);
      $[49] = t23;
      $[50] = t24;
    } else {
      t24 = $[50];
    }
    let t25;
    if ($[51] !== t) {
      t25 = t("title");
      $[51] = t;
      $[52] = t25;
    } else {
      t25 = $[52];
    }
    let t26;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t26 = <ChevronsUpDown className="size-4" />;
      $[53] = t26;
    } else {
      t26 = $[53];
    }
    if ($[54] !== t24 || $[55] !== t25) {
      t9 = <CollapsibleTrigger className={t24}>{t25}{t26}</CollapsibleTrigger>;
      $[54] = t24;
      $[55] = t25;
      $[56] = t9;
    } else {
      t9 = $[56];
    }
    T0 = CollapsibleContent;
    t3 = "p-2 pt-0";
    t1 = "flex flex-col gap-2";
    t2 = legends.map((legend, index) => <li className="flex items-center gap-2" key={legend.id}><div className={cn("size-4 rounded-full bg-linear-to-br", legend.color)} /><span className="flex flex-1 items-center justify-between text-muted-foreground text-xs">{legend.label}{legend.hasInfo && <CircleQuestionMarkIcon id={index === legends.findIndex(_temp) ? "tour-legend-detail" : undefined} onClick={() => {
          setSelectedLegendId(legend.id as LegendId);
          setIsDialogOpen(true);
        }} className="size-4 shrink-0 cursor-pointer text-muted-foreground" />}</span></li>);
    $[0] = dir;
    $[1] = isOpen;
    $[2] = t;
    $[3] = viewMode;
    $[4] = T0;
    $[5] = T1;
    $[6] = legendInfo;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    T0 = $[4];
    T1 = $[5];
    legendInfo = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
    t6 = $[12];
    t7 = $[13];
    t8 = $[14];
    t9 = $[15];
  }
  let t10;
  if ($[57] !== t1 || $[58] !== t2) {
    t10 = <ul className={t1}>{t2}</ul>;
    $[57] = t1;
    $[58] = t2;
    $[59] = t10;
  } else {
    t10 = $[59];
  }
  let t11;
  if ($[60] !== T0 || $[61] !== t10 || $[62] !== t3) {
    t11 = <T0 className={t3}>{t10}</T0>;
    $[60] = T0;
    $[61] = t10;
    $[62] = t3;
    $[63] = t11;
  } else {
    t11 = $[63];
  }
  let t12;
  if ($[64] !== T1 || $[65] !== t11 || $[66] !== t4 || $[67] !== t5 || $[68] !== t6 || $[69] !== t7 || $[70] !== t8 || $[71] !== t9) {
    t12 = <T1 id={t4} dir={t5} open={t6} onOpenChange={t7} className={t8}>{t9}{t11}</T1>;
    $[64] = T1;
    $[65] = t11;
    $[66] = t4;
    $[67] = t5;
    $[68] = t6;
    $[69] = t7;
    $[70] = t8;
    $[71] = t9;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  let t13;
  if ($[73] !== isDialogOpen || $[74] !== legendInfo || $[75] !== selectedLegendId) {
    t13 = isDialogOpen && selectedLegendId && <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}><DialogContent><DialogHeader className="-mx-4 flex flex-row items-center gap-2 border-b px-4 pb-2"><CircleQuestionMarkIcon className="text-muted-foreground" /><DialogTitle>{legendInfo[selectedLegendId].title}</DialogTitle><DialogDescription className="sr-only">{legendInfo[selectedLegendId].description}</DialogDescription></DialogHeader><p>{legendInfo[selectedLegendId].description}</p></DialogContent></Dialog>;
    $[73] = isDialogOpen;
    $[74] = legendInfo;
    $[75] = selectedLegendId;
    $[76] = t13;
  } else {
    t13 = $[76];
  }
  let t14;
  if ($[77] !== t12 || $[78] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[77] = t12;
    $[78] = t13;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  return t14;
}
function _temp(l) {
  return l.hasInfo;
}